import React, { useState, useEffect, useCallback } from 'react';

// Interfaces for both components
interface RiskItem {
  id?: number;
  itemType: string;
  description: string;
  itemName: string;
  insuredValue: string;
  premium: string;
  serialNumber: string;
  coverType: string;
  noClaimsBonus: string;
  excess: string;
  isEditing?: boolean;
  category?: string;
  item_type?: string;
  item_name?: string;
  serial_number?: string;
  cover_type?: string;
  no_claims_bonus?: string;
  insured_value?: string | number;
}

interface QuoteRiskItemsModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  clientId: number;
  setSaveStatus: (status: 'idle' | 'success' | 'error') => void;
  setSaveMessage: (message: string) => void;
  riskItem?: any;
  mode?: 'create' | 'edit' | 'view';
}

interface EditableRiskItemCardProps {
  riskItem: RiskItem;
  index: number;
  onSave: (index: number, updatedData: any) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
  cardNumber?: number;
  cardTitle?: string;
  showCardNumber?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL;

// Currency formatting helper functions
const formatCurrencyInput = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  // Convert to string if it's a number
  const stringValue = typeof value === 'number' ? value.toString() : value;
  
  // Remove any existing formatting
  const numericValue = stringValue.replace(/[^\d.]/g, '');
  
  // Split into whole and decimal parts
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  
  // Limit decimal to 2 digits
  if (decimalPart.length > 2) {
    decimalPart = decimalPart.substring(0, 2);
  }
  
  // Format the whole number part with commas
  if (wholePart) {
    wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  }
  
  // Combine parts
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  // Remove commas and parse as float
  return parseFloat(value.replace(/,/g, ''));
};

const formatCurrencyForDisplay = (value: number | string): string => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) return value.toString();
  
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const mapNoClaimsBonusForBackend = (frontendValue: string): string => {
  console.log('🔍 Mapping no claims bonus:', frontendValue);
  
  // Handle both "120 Months (10 Year)" and "120Months (10 Year)"
  const normalizedValue = frontendValue.replace(/(\d+)\s*Months\s*\(\s*(\d+)\s*Year\s*\)/i, (match, months, years) => {
    return `${months} Months (${years} Year)`;
  });

  const mapping: { [key: string]: string } = {
    "None": "None",
    "12 Months (1 Year)": "1 Year",
    "24 Months (2 Year)": "2 Year",
    "36 Months (3 Year)": "3 Year",
    "48 Months (4 Year)": "4 Year",
    "60 Months (5 Year)": "5 Year Plus",
    "72 Months (6 Year)": "5 Year Plus",
    "84 Months (7 Year)": "5 Year Plus",
    "96 Months (8 Year)": "5 Year Plus",
    "108 Months (9 Year)": "5 Year Plus",
    "120 Months (10 Year)": "5 Year Plus"
  };
  
  const result = mapping[normalizedValue] || "None";
  console.log('✅ Mapped result:', result);
  return result;
};

// Main Modal Component
export const QuoteRiskItemsModal: React.FC<QuoteRiskItemsModalProps> = ({
  isVisible,
  onClose,
  onSave,
  clientId,
  setSaveStatus,
  setSaveMessage,
  riskItem,
  mode = 'create'
}) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Helper function to map API data to form data
  const mapApiDataToForm = (apiData: any) => {
    if (!apiData) {
      return {
        itemType: '',
        description: '',
        itemName: '',
        insuredValue: '',
        premium: '',
        serialNumber: '',
        coverType: '',
        noClaimsBonus: '',
        excess: '',
      };
    }
    
    console.log('🔍 Mapping Risk Item API data:', apiData);
    
    // Accept both the display string ("48 Months (4 Year)") and the
    // legacy short format ("4 Year"). If it's already a valid display
    // value, keep it as-is — only fall back for legacy rows.
    const rawBonus = riskItem?.noClaimsBonus || riskItem?.no_claims_bonus || '';

    const legacyMap: { [key: string]: string } = {
      '1 Year': '12 Months (1 Year)',
      '2 Year': '24 Months (2 Year)',
      '3 Year': '36 Months (3 Year)',
      '4 Year': '48 Months (4 Year)',
      '5 Year Plus': '60 Months (5 Year)',
    };

    const validDisplayValues = [
      'None',
      '12 Months (1 Year)',
      '24 Months (2 Year)',
      '36 Months (3 Year)',
      '48 Months (4 Year)',
      '60 Months (5 Year)',
      '72 Months (6 Year)',
      '84 Months (7 Year)',
      '96 Months (8 Year)',
      '108 Months (9 Year)',
      '120 Months (10 Year)',
    ];

    let frontendNoClaimsBonus = rawBonus;
    if (!validDisplayValues.includes(rawBonus)) {
      frontendNoClaimsBonus = legacyMap[rawBonus] || '';
    }
    
    return {
      itemType: riskItem.itemType || riskItem.item_type || riskItem.category || '',
      description: riskItem.description || '',
      itemName: riskItem.itemName || riskItem.item_name || '',
      insuredValue: riskItem.insuredValue || riskItem.insured_value || '',
      premium: riskItem.premium || '',
      serialNumber: riskItem.serialNumber || riskItem.serial_number || '',
      coverType: riskItem.coverType || riskItem.cover_type || '',
      noClaimsBonus: frontendNoClaimsBonus, // Use mapped frontend format
      excess: riskItem.excess || '',
    };
  };

  // Initialize form data based on mode
  const getInitialFormData = useCallback(() => {
    console.log('🔄 Getting initial form data for mode:', mode);
    console.log('📦 Received riskItem data:', riskItem);
    
    if (isEditMode || isViewMode) {
      const mappedData = mapApiDataToForm(riskItem);
      console.log('✅ Mapped form data:', mappedData);
      return mappedData;
    }
    
    // For create mode, use empty defaults
    return {
      itemType: '',
      description: '',
      itemName: '',
      insuredValue: '',
      premium: '',
      serialNumber: '',
      coverType: '',
      noClaimsBonus: '',
      excess: '',
    };
  }, [riskItem, mode, isEditMode, isViewMode]);

  const [formData, setFormData] = useState(getInitialFormData);

  const [currencyDisplay, setCurrencyDisplay] = useState(() => {
    const initialData = getInitialFormData();
    
    const insuredValue = initialData.insuredValue ? 
      (typeof initialData.insuredValue === 'string' ? 
        parseFloat(initialData.insuredValue) : 
        Number(initialData.insuredValue)) : 0;
    
    const premium = initialData.premium ? 
      (typeof initialData.premium === 'string' ? 
        parseFloat(initialData.premium) : 
        Number(initialData.premium)) : 0;
    
    const excess = initialData.excess ? 
      (typeof initialData.excess === 'string' ? 
        parseFloat(initialData.excess) : 
        Number(initialData.excess)) : 0;
    
    return {
      insuredValue: insuredValue > 0 ? formatCurrencyForDisplay(insuredValue) : '',
      premium: premium > 0 ? formatCurrencyForDisplay(premium) : '',
      excess: excess > 0 ? formatCurrencyForDisplay(excess) : '',
    };
  });

  const [errors, setErrors] = useState({
    itemType: '',
    description: '',
    itemName: '',
    insuredValue: '',
    premium: '',
    serialNumber: '',
    coverType: '',
    noClaimsBonus: '',
    excess: '',
  });

  // Reset form when mode or riskItem changes
  useEffect(() => {
    console.log('🔄 useEffect triggered - isVisible:', isVisible, 'mode:', mode);
    const initialData = getInitialFormData();
    console.log('📝 Setting form data to:', initialData);
    
    setFormData(initialData);
    
    // Ensure currency values are properly handled
    const insuredValueNum = initialData.insuredValue ? 
      (typeof initialData.insuredValue === 'string' ? 
        parseFloat(initialData.insuredValue) : 
        Number(initialData.insuredValue)) : 0;
    
    const premiumNum = initialData.premium ? 
      (typeof initialData.premium === 'string' ? 
        parseFloat(initialData.premium) : 
        Number(initialData.premium)) : 0;
    
    const excessNum = initialData.excess ? 
      (typeof initialData.excess === 'string' ? 
        parseFloat(initialData.excess) : 
        Number(initialData.excess)) : 0;
    
    console.log('💰 Currency values:', {
      insuredValue: insuredValueNum,
      premium: premiumNum,
      excess: excessNum
    });
    
    setCurrencyDisplay({
      insuredValue: insuredValueNum > 0 ? formatCurrencyForDisplay(insuredValueNum) : '',
      premium: premiumNum > 0 ? formatCurrencyForDisplay(premiumNum) : '',
      excess: excessNum > 0 ? formatCurrencyForDisplay(excessNum) : '',
    });
  }, [riskItem, mode, isVisible, getInitialFormData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (isViewMode) return; // Don't allow changes in view mode
    
    const { name, value } = e.target;
    
    if (name === 'insuredValue' || name === 'premium' || name === 'excess') {
      // Handle currency fields with formatting
      const formattedValue = formatCurrencyInput(value);
      const numericValue = parseCurrencyInput(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue ? numericValue.toString() : '',
      }));
      
      setCurrencyDisplay(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // blur handlers for final formatting
  const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {
      itemType: isCreateMode && !formData.itemType ? 'Item Type is required' : '',
      description: isCreateMode && !formData.description.trim() ? 'Description is required' : '',
      itemName: isCreateMode && !formData.itemName.trim() ? 'Name is required' : '',
      insuredValue: isCreateMode && !formData.insuredValue ? 'Insured Value is required' : 
                   formData.insuredValue && isNaN(parseFloat(formData.insuredValue)) ? 'Insured Value must be a valid number' : 
                   formData.insuredValue && parseFloat(formData.insuredValue) < 0 ? 'Insured Value cannot be negative' : '',
      premium: formData.premium && isNaN(parseFloat(formData.premium)) ? 'Premium must be a valid number' : 
               formData.premium && parseFloat(formData.premium) < 0 ? 'Premium cannot be negative' : '',
      serialNumber: '',
      coverType: isCreateMode && !formData.coverType ? 'Cover Type is required' : '',
      noClaimsBonus: isCreateMode && !formData.noClaimsBonus ? 'No Claims Bonus is required' : '',
      excess: formData.excess && isNaN(parseFloat(formData.excess)) ? 'Excess must be a valid number' : 
              formData.excess && parseFloat(formData.excess) < 0 ? 'Excess cannot be negative' : '',
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isViewMode) return;
    
    console.log('✅ Submit button clicked', formData);
    console.log('👤 Client ID:', clientId);
    
    if (!validateForm()) {
      console.log('❌ Validation failed', errors);
      return;
    }

    try {
      // Keep the user-facing value as-is. No mapping to short forms.
      const backendNoClaimsBonus = formData.noClaimsBonus || '';

      // Ensure ALL fields have values - even if empty strings
      const riskItemForState: any = {
        // Required fields - ensure they're not undefined
        itemType: formData.itemType || '',
        description: formData.description || '',
        itemName: formData.itemName || '',
        insuredValue: formData.insuredValue ? parseFloat(formData.insuredValue) : 0,
        premium: formData.premium ? parseFloat(formData.premium) : 0,
        serialNumber: formData.serialNumber || '',
        coverType: formData.coverType || '',
        noClaimsBonus: formData.noClaimsBonus || '', // Keep frontend version "48 Months (4 Year)"
        excess: formData.excess ? parseFloat(formData.excess) : 0,
        // Metadata
        id: isEditMode && riskItem?.id ? riskItem.id : `temp-${Date.now()}`,
        client_id: clientId,
        isTemp: isCreateMode ? true : riskItem?.isTemp,
        // Add snake_case versions for backend compatibility
        item_type: formData.itemType || '',
        item_name: formData.itemName || '',
        serial_number: formData.serialNumber || '',
        cover_type: formData.coverType || '',
        no_claims_bonus: backendNoClaimsBonus, // Same display string ("48 Months (4 Year)")
        insured_value: formData.insuredValue ? parseFloat(formData.insuredValue) : 0
      };

      console.log('🚀 Calling onSave with complete data:', riskItemForState);
      console.log('🔍 No Claims Bonus fields:', {
        frontend_display: riskItemForState.noClaimsBonus,
        backend_field: riskItemForState.no_claims_bonus
      });
      console.log('🔍 ALL snake_case fields:', {
        item_type: riskItemForState.item_type,
        item_name: riskItemForState.item_name,
        serial_number: riskItemForState.serial_number,
        cover_type: riskItemForState.cover_type,
        no_claims_bonus: riskItemForState.no_claims_bonus, // <-- This should be "4 Year"
        insured_value: riskItemForState.insured_value
      });
      
      // Call onSave with the local state object
      onSave(riskItemForState);
      setSaveStatus('success');
      setSaveMessage(isEditMode ? 'Risk Item updated successfully!' : 'Risk Item added successfully!');
      
      // Close the modal after a short delay
      setTimeout(() => {
        handleClose();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error saving risk item:', error);
      setSaveStatus('error');
      setSaveMessage(`Failed to ${isEditMode ? 'update' : 'add'} risk item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData(getInitialFormData());
    setCurrencyDisplay({
      insuredValue: '',
      premium: '',
      excess: '',
    });
    setErrors({
      itemType: '',
      description: '',
      itemName: '',
      insuredValue: '',
      premium: '',
      serialNumber: '',
      coverType: '',
      noClaimsBonus: '',
      excess: '',
    });
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">
          {isViewMode ? 'View Risk Item Details' : 
           isEditMode ? 'Edit Risk Item Details' : 'Add All Risk Item'}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            {/* Row 1: Category, Name, Serial Number */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="itemType" className="block text-gray-700 font-bold mb-2 text-sm">
                  Category
                </label>
                <select
                  id="itemType"
                  name="itemType"
                  value={formData.itemType}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="">Select item type</option>
                  <option value="Unspecified">Unspecified</option>
                  <option value="Other">Other</option>
                  <option value="Artwork and Antiques">Artwork and Antiques</option>
                  <option value="Blanket Cover">Blanket Cover</option>
                  <option value="Camera / Equipment">Camera / Equipment</option>
                  <option value="Car Radio">Car Radio</option>
                  <option value="Camping Equipment">Camping Equipment</option>
                  <option value="Caravan Content">Caravan Content</option>
                  <option value="Mobile Phone">Mobile Phone</option>
                  <option value="Mobile Phone Accessories">Mobile Phone Accessories</option>
                  <option value="Collectables">Collectables</option>
                  <option value="Computer">Computer</option>
                  <option value="Contact Lenses">Contact Lenses</option>
                  <option value="Diving Equipment">Diving Equipment</option>
                  <option value="Drones">Drones</option>
                  <option value="Firearm">Firearm</option>
                  <option value="Fishing Equipment">Fishing Equipment</option>
                  <option value="Golf Cart">Golf Cart</option>
                  <option value="Golf Equipment">Golf Equipment</option>
                  <option value="Goods In Vault">Goods In Vault</option>
                  <option value="GPS">GPS</option>
                  <option value="Hearing Aid">Hearing Aid</option>
                  <option value="Hearing Aid">Household Goods In Transit</option>
                  <option value="Jewellery">Jewellery</option>
                  <option value="Keys, Locks and Remote Control Units">Keys, Locks and Remote Control Units</option>
                  <option value="Laptops">Laptops</option>
                  <option value="Musical Instruments">Musical Instruments</option>
                  <option value="Musical Instruments Professional">Musical Instruments Professional</option>
                  <option value="Pedal Cycles">Pedal Cycles</option>
                  <option value="Personal Documents">Personal Documents</option>
                  <option value="Portable Radio">Portable Radio</option>
                  <option value="Portable TV">Portable TV</option>
                  <option value="Perscription Glasses">Perscription Glasses</option>
                  <option value="Quad Bike">Quad Bike</option>
                  <option value="Sport Equipment">Sport Equipment</option>
                  <option value="Sunglasses">Sunglasses</option>
                  <option value="Tablet-Mobile Device">Tablet-Mobile Device</option>
                  <option value="Tracking Device">Tracking Device</option>
                  <option value="Watch-Smart Mobile Device">Watch-Smart Mobile Device</option>
                </select>
                {errors.itemType && <p className="text-red-500 text-xs mt-1">{errors.itemType}</p>}
              </div>
              <div>
                <label htmlFor="itemName" className="block text-gray-700 font-bold mb-2 text-sm">
                  Name
                </label>
                <input
                  type="text"
                  id="itemName"
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="Enter item name"
                  disabled={isViewMode}
                />
                {errors.itemName && <p className="text-red-500 text-xs mt-1">{errors.itemName}</p>}
              </div>

              <div>
                <label htmlFor="serialNumber" className="block text-gray-700 font-bold mb-2 text-sm">
                  Serial Number
                </label>
                <input
                  type="text"
                  id="serialNumber"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="Enter serial number"
                  disabled={isViewMode}
                />
                {errors.serialNumber && <p className="text-red-500 text-xs mt-1">{errors.serialNumber}</p>}
              </div>
            </div>

            {/* Row 2: Description */}
            <div>
              <label htmlFor="description" className="block text-gray-700 font-bold mb-2 text-sm">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                rows={4}
                placeholder="Enter item description"
                disabled={isViewMode}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Row 3: Cover Type, No Claims Bonus */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="coverType" className="block text-gray-700 font-bold mb-2 text-sm">
                  Cover Type
                </label>
                <select
                  id="coverType"
                  name="coverType"
                  value={formData.coverType}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="">Select cover type</option>
                  <option value="Comprehensive">Comprehensive</option>
                  <option value="Compulsory Third Party">Compulsory Third Party</option>
                  <option value="Third Party Fire and Theft">Third Party Fire and Theft</option>
                  <option value="Third Party Property Damage">Third Party Property Damage</option>
                </select>
                {errors.coverType && <p className="text-red-500 text-xs mt-1">{errors.coverType}</p>}
              </div>
              <div>
                <label htmlFor="noClaimsBonus" className="block text-gray-700 font-bold mb-2 text-sm">
                  No Claims Bonus
                </label>
                <select
                  id="noClaimsBonus"
                  name="noClaimsBonus"
                  value={formData.noClaimsBonus}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="">Select no claims bonus</option>
                  <option value="None">None</option>
                  <option value="12 Months (1 Year)">12 Months (1 Year)</option>
                  <option value="24 Months (2 Year)">24 Months (2 Year)</option>
                  <option value="36 Months (3 Year)">36 Months (3 Year)</option>
                  <option value="48 Months (4 Year)">48 Months (4 Year)</option>
                  <option value="60 Months (5 Year)">60 Months (5 Year)</option>
                  <option value="72 Months (6 Year)">72 Months (6 Year)</option>
                  <option value="84 Months (7 Year)">84 Months (7 Year)</option>
                  <option value="96 Months (8 Year)">96 Months (8 Year)</option>
                  <option value="108 Months (9 Year)">108 Months (9 Year)</option>
                  <option value="120 Months (10 Year)">120 Months (10 Year)</option>
                </select>
                {errors.noClaimsBonus && <p className="text-red-500 text-xs mt-1">{errors.noClaimsBonus}</p>}
              </div>
            </div>

            {/* Row 4: Insured Value, Premium, Excess */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="insuredValue" className="block text-gray-700 font-bold mb-2 text-sm">
                  Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="text"
                    id="insuredValue"
                    name="insuredValue"
                    value={currencyDisplay.insuredValue}
                    onChange={handleChange}
                    onBlur={() => handleCurrencyBlur('insuredValue')}
                    className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>
                {errors.insuredValue && <p className="text-red-500 text-xs mt-1">{errors.insuredValue}</p>}
              </div>

              <div>
                <label htmlFor="premium" className="block text-gray-700 font-bold mb-2 text-sm">
                  Premium <span className="font-normal italic text-gray-500">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="text"
                    id="premium"
                    name="premium"
                    value={currencyDisplay.premium}
                    onChange={handleChange}
                    onBlur={() => handleCurrencyBlur('premium')}
                    className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>
                {errors.premium && <p className="text-red-500 text-xs mt-1">{errors.premium}</p>}
              </div>

              <div>
                <label htmlFor="excess" className="block text-gray-700 font-bold mb-2 text-sm">
                  Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="text"
                    id="excess"
                    name="excess"
                    value={currencyDisplay.excess}
                    onChange={handleChange}
                    onBlur={() => handleCurrencyBlur('excess')}
                    className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>
                {errors.excess && <p className="text-red-500 text-xs mt-1">{errors.excess}</p>}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
              >
                {isEditMode ? 'Update Risk Item' : 'Submit Risk Item'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// Editable Card Component
export const EditableRiskItemCard: React.FC<EditableRiskItemCardProps> = ({
  riskItem,
  index,
  onSave,
  onCancel,
  onEdit,
  cardNumber,
  cardTitle = 'Risk Item',
  showCardNumber = true
}) => {
  const [formData, setFormData] = useState(() => {
    console.log('📦 Initializing risk item card form data:', riskItem);
    return {
      itemType: riskItem.itemType || riskItem.item_type || riskItem.category || '',
      description: riskItem.description || '',
      itemName: riskItem.itemName || riskItem.item_name || '',
      insuredValue: riskItem.insuredValue || riskItem.insured_value || '',
      premium: riskItem.premium || '',
      serialNumber: riskItem.serialNumber || riskItem.serial_number || '',
      coverType: riskItem.coverType || riskItem.cover_type || '',
      noClaimsBonus: riskItem.noClaimsBonus || riskItem.no_claims_bonus || '',
      excess: riskItem.excess || '',
    };
  });

  const [currencyDisplay, setCurrencyDisplay] = useState(() => {
    const insuredValue = riskItem.insuredValue || riskItem.insured_value || '';
    const premium = riskItem.premium || '';
    const excess = riskItem.excess || '';
    
    // Convert to numbers if needed
    const insuredNum = insuredValue ? 
      (typeof insuredValue === 'string' ? parseFloat(insuredValue) : Number(insuredValue)) : 0;
    const premiumNum = premium ? 
      (typeof premium === 'string' ? parseFloat(premium) : Number(premium)) : 0;
    const excessNum = excess ? 
      (typeof excess === 'string' ? parseFloat(excess) : Number(excess)) : 0;
    
    return {
      insuredValue: insuredNum > 0 ? formatCurrencyForDisplay(insuredNum) : '',
      premium: premiumNum > 0 ? formatCurrencyForDisplay(premiumNum) : '',
      excess: excessNum > 0 ? formatCurrencyForDisplay(excessNum) : '',
    };
  });

  const [errors, setErrors] = useState({
    itemType: '',
    description: '',
    itemName: '',
    insuredValue: '',
    premium: '',
    serialNumber: '',
    coverType: '',
    noClaimsBonus: '',
    excess: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'insuredValue' || name === 'premium' || name === 'excess') {
      const formattedValue = formatCurrencyInput(value);
      const numericValue = parseCurrencyInput(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue ? numericValue.toString() : '',
      }));
      
      setCurrencyDisplay(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const validateForm = () => {
    // No validation for editing - all fields are optional
    const newErrors = {
      itemType: '',
      description: '',
      itemName: '',
      insuredValue: '',
      premium: '',
      serialNumber: '',
      coverType: '',
      noClaimsBonus: '',
      excess: '',
    };
    
    setErrors(newErrors);
    return true; // Always return true for editing
  };

  const handleSave = () => {
    if (!validateForm()) return;
    
    // Helper function to safely convert to number
    const safeToNumber = (value: any): number => {
      if (value === null || value === undefined || value === '') return 0;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') return parseFloat(value) || 0;
      return 0;
    };

    // Use the exact same value the user sees in the dropdown —
    // no mapping to short forms. This is what the display card and the
    // edit modal read back, so both stay in sync with the saved data.
    const normalizedBonus = formData.noClaimsBonus || riskItem.noClaimsBonus || riskItem.no_claims_bonus || '';

    const updatedData = {
      itemType: formData.itemType || riskItem.itemType,
      description: formData.description || riskItem.description,
      itemName: formData.itemName || riskItem.itemName,
      insuredValue: formData.insuredValue ? safeToNumber(formData.insuredValue) : safeToNumber(riskItem.insuredValue),
      premium: formData.premium ? safeToNumber(formData.premium) : safeToNumber(riskItem.premium),
      serialNumber: formData.serialNumber || riskItem.serialNumber,
      coverType: formData.coverType || riskItem.coverType,
      noClaimsBonus: normalizedBonus,
      no_claims_bonus: normalizedBonus, // Same value, both casings
      excess: formData.excess ? safeToNumber(formData.excess) : safeToNumber(riskItem.excess),
      id: riskItem.id,
      isEditing: false
    };
    
    onSave(index, updatedData);
  };

  const handleCancel = () => {
    onCancel(index);
  };

  // If not in editing mode, show the display card
  if (!riskItem.isEditing) {
    // Safe currency formatter
    const formatCurrency = (value: any): string => {
      if (value === null || value === undefined || value === '') return 'Not set';
      
      try {
        // Convert to number safely
        const numValue = typeof value === 'string' ? 
                        parseFloat(value) : 
                        Number(value);
        return isNaN(numValue) ? 'Not set' : `R ${numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;
      } catch {
        return 'Not set';
      }
    };

    // Safe field getter that handles all possible field names
    const getField = (field: keyof RiskItem, fallback: string = 'Not specified'): string => {
      const value = riskItem[field];
      return value !== null && value !== undefined && value !== '' ? String(value) : fallback;
    };

    // Get category from any possible field name
    const getCategory = (): string => {
      return riskItem.itemType || riskItem.category || riskItem.item_type || 'Not specified';
    };

    {riskItem.serialNumber && riskItem.serialNumber !== '' && (
      <div className="flex justify-between">
        <span className="text-gray-600">Serial Number:</span>
        <span className="font-medium">{riskItem.serialNumber}</span>
      </div>
    )}

  // Update the description check:
  {riskItem.description && riskItem.description !== '' && (
    <div className="flex flex-col">
      <span className="text-gray-600 mb-1">Description:</span>
      <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border">
        {riskItem.description}
      </span>
    </div>
  )}

  // Update the coverType check:
  {riskItem.coverType && riskItem.coverType !== '' && (
    <div className="flex justify-between">
      <span className="text-gray-600">Cover Type:</span>
      <span className="font-medium">{riskItem.coverType}</span>
    </div>
  )}

  // Update the noClaimsBonus check:
  {riskItem.noClaimsBonus && riskItem.noClaimsBonus !== '' && (
    <div className="flex justify-between">
      <span className="text-gray-600">No Claims Bonus:</span>
      <span className="font-medium">{riskItem.noClaimsBonus}</span>
    </div>
  )}

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-bold text-gray-800">
              {showCardNumber ? `${cardTitle} #${cardNumber || (index + 1)}` : cardTitle}
            </h4>
          </div>

          <div className="space-y-2 text-sm">
            {/* Category - Fixed: Use the safe getter function */}
            <div className="flex justify-between">
              <span className="text-gray-600">Category:</span>
              <span className="font-medium text-right max-w-[150px]">
                {getCategory()}
              </span>
            </div>
            
            {/* Serial Number */}
            {riskItem.serialNumber && (
              <div className="flex justify-between">
                <span className="text-gray-600">Serial Number:</span>
                <span className="font-medium">{riskItem.serialNumber}</span>
              </div>
            )}
            
            {/* Description */}
            {riskItem.description && (
              <div className="flex flex-col">
                <span className="text-gray-600 mb-1">Description:</span>
                <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border">
                  {riskItem.description}
                </span>
              </div>
            )}
            
            {/* Insured Value */}
            <div className="flex justify-between">
              <span className="text-gray-600">Insured Value:</span>
              <span className="font-medium text-green-600">
                {formatCurrency(riskItem.insuredValue)}
              </span>
            </div>
            
            {/* Premium */}
            <div className="flex justify-between">
              <span className="text-gray-600">Premium:</span>
              <span className="font-medium text-purple-600">
                {formatCurrency(riskItem.premium)}
              </span>
            </div>

            {/* Cover Type */}
            {riskItem.coverType && (
              <div className="flex justify-between">
                <span className="text-gray-600">Cover Type:</span>
                <span className="font-medium">{riskItem.coverType}</span>
              </div>
            )}

            {/* No Claims Bonus */}
            {riskItem.noClaimsBonus && (
              <div className="flex justify-between">
                <span className="text-gray-600">No Claims Bonus:</span>
                <span className="font-medium">{riskItem.noClaimsBonus}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Editing mode - show the form
  return (
    <div className="bg-white border-2 border-blue-200 rounded-lg shadow-md p-4 mb-4">
      <h4 className="font-bold text-lg mb-4 text-blue-700">Edit Risk Item</h4>
      
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="space-y-4 mb-6">
          {/* Row 1: Category, Name, Serial Number */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={`itemType-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Item
              </label>
              <select
                id={`itemType-${index}`}
                name="itemType"
                value={formData.itemType}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="">Select item type</option>
                <option value="Unspecified">Unspecified</option>
                <option value="Other">Other</option>
                <option value="Artwork and Antiques">Artwork and Antiques</option>
                <option value="Blanket Cover">Blanket Cover</option>
                <option value="Camera / Equipment">Camera / Equipment</option>
                <option value="Car Radio">Car Radio</option>
                <option value="Camping Equipment">Camping Equipment</option>
                <option value="Caravan Content">Caravan Content</option>
                <option value="Mobile Phone">Mobile Phone</option>
                <option value="Mobile Phone Accessories">Mobile Phone Accessories</option>
                <option value="Collectables">Collectables</option>
                <option value="Computer">Computer</option>
                <option value="Contact Lenses">Contact Lenses</option>
                <option value="Diving Equipment">Diving Equipment</option>
                <option value="Drones">Drones</option>
                <option value="Firearm">Firearm</option>
                <option value="Fishing Equipment">Fishing Equipment</option>
                <option value="Golf Cart">Golf Cart</option>
                <option value="Golf Equipment">Golf Equipment</option>
                <option value="Goods In Vault">Goods In Vault</option>
                <option value="GPS">GPS</option>
                <option value="Hearing Aid">Hearing Aid</option>
                <option value="Hearing Aid">Household Goods In Transit</option>
                <option value="Jewellery">Jewellery</option>
                <option value="Keys, Locks and Remote Control Units">Keys, Locks and Remote Control Units</option>
                <option value="Laptops">Laptops</option>
                <option value="Musical Instruments">Musical Instruments</option>
                <option value="Musical Instruments Professional">Musical Instruments Professional</option>
                <option value="Pedal Cycles">Pedal Cycles</option>
                <option value="Personal Documents">Personal Documents</option>
                <option value="Portable Radio">Portable Radio</option>
                <option value="Portable TV">Portable TV</option>
                <option value="Perscription Glasses">Perscription Glasses</option>
                <option value="Quad Bike">Quad Bike</option>
                <option value="Sport Equipment">Sport Equipment</option>
                <option value="Sunglasses">Sunglasses</option>
                <option value="Tablet-Mobile Device">Tablet-Mobile Device</option>
                <option value="Tracking Device">Tracking Device</option>
                <option value="Watch-Smart Mobile Device">Watch-Smart Mobile Device</option>
              </select>
            </div>
            <div>
              <label htmlFor={`itemName-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Name
              </label>
              <input
                type="text"
                id={`itemName-${index}`}
                name="itemName"
                value={formData.itemName}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="Enter item name"
              />
            </div>

            <div>
              <label htmlFor={`serialNumber-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Serial Number
              </label>
              <input
                type="text"
                id={`serialNumber-${index}`}
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="Enter serial number"
              />
            </div>
          </div>

          {/* Row 2: Description */}
          <div>
            <label htmlFor={`description-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
              Description
            </label>
            <textarea
              id={`description-${index}`}
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              rows={4}
              placeholder="Enter item description"
            />
          </div>

          {/* Row 3: Cover Type, No Claims Bonus */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={`coverType-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Cover Type
              </label>
              <select
                id={`coverType-${index}`}
                name="coverType"
                value={formData.coverType}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="">Select cover type</option>
                <option value="Comprehensive">Comprehensive</option>
                <option value="Compulsory Third Party">Compulsory Third Party</option>
                <option value="Third Party Fire and Theft">Third Party Fire and Theft</option>
                <option value="Third Party Property Damage">Third Party Property Damage</option>
              </select>
            </div>
            <div>
              <label htmlFor={`noClaimsBonus-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                No Claims Bonus
              </label>
              <select
                id={`noClaimsBonus-${index}`}
                name="noClaimsBonus"
                value={formData.noClaimsBonus}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="">Select no claims bonus</option>
                <option value="None">None</option>
                <option value="12 Months (1 Year)">12 Months (1 Year)</option>
                <option value="24 Months (2 Year)">24 Months (2 Year)</option>
                <option value="36 Months (3 Year)">36 Months (3 Year)</option>
                <option value="48 Months (4 Year)">48 Months (4 Year)</option>
                <option value="60 Months (5 Year)">60 Months (5 Year)</option>
                <option value="72 Months (6 Year)">72 Months (6 Year)</option>
                <option value="84 Months (7 Year)">84 Months (7 Year)</option>
                <option value="96 Months (8 Year)">96 Months (8 Year)</option>
                <option value="108 Months (9 Year)">108 Months (9 Year)</option>
                <option value="120 Months (10 Year)">120 Months (10 Year)</option>
              </select>
            </div>
          </div>

          {/* Row 4: Insured Value, Premium, Excess */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={`insuredValue-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Insured Value
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`insuredValue-${index}`}
                  name="insuredValue"
                  value={currencyDisplay.insuredValue}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('insuredValue')}
                  className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label htmlFor={`premium-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Premium
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`premium-${index}`}
                  name="premium"
                  value={currencyDisplay.premium}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('premium')}
                  className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label htmlFor={`excess-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Excess
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`excess-${index}`}
                  name="excess"
                  value={currencyDisplay.excess}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('excess')}
                  className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuoteRiskItemsModal;